import { useState, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { HelpCircle, Sparkles } from 'lucide-react'
import { useAudio } from '../../hooks/useAudio'
import { useSparkle } from '../../hooks/useSparkle'

interface PatternCompleteProps {
  pattern: (number | string)[]
  options: number[]
  onComplete: (correct: boolean) => void
}

export default function PatternComplete({ pattern, options, onComplete }: PatternCompleteProps) {
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null)
  const [showFeedback, setShowFeedback] = useState(false)
  const [isCorrect, setIsCorrect] = useState(false)
  const [countdown, setCountdown] = useState(0)
  
  const { playCorrect, playWrong } = useAudio()
  const { miniBurst } = useSparkle()

  // Find the missing value
  const missingIndex = pattern.findIndex(p => p === '___')
  const correctAnswer = typeof pattern[missingIndex] === 'number' 
    ? pattern[missingIndex] as number
    : options.find(opt => {
        const nums = pattern.filter(p => typeof p === 'number') as number[]
        if (nums.length >= 2) {
          const diff = nums[1] - nums[0]
          return opt === nums[missingIndex > 0 ? missingIndex - 1 : 0] + diff
        }
        return false
      }) || options[0]

  const handleSelect = useCallback((answer: number) => {
    if (showFeedback) return

    const correct = answer === correctAnswer
    setSelectedAnswer(answer)
    setIsCorrect(correct)
    setShowFeedback(true)
    
    // Play sound and effects
    if (correct) {
      playCorrect()
      miniBurst(0.5, 0.5)
    } else {
      playWrong()
    }
    
    // Differentiated timing: wrong answers get more time
    const feedbackDuration = correct ? 1200 : 2000
    setCountdown(Math.ceil(feedbackDuration / 1000))

    setTimeout(() => {
      onComplete(correct)
    }, feedbackDuration)
  }, [onComplete, showFeedback, correctAnswer, playCorrect, playWrong, miniBurst])
  
  // Countdown timer for visual feedback
  useEffect(() => {
    if (!showFeedback || countdown <= 0) return
    
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    
    return () => clearInterval(timer)
  }, [showFeedback, countdown])

  return (
    <div className="w-full max-w-sm mx-auto px-3">
      {/* Compact Header */}
      <motion.div 
        className="text-center mb-3"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        <div className="inline-flex items-center gap-1.5 bg-[var(--math-purple)]/10 px-3 py-1.5 rounded-full mb-2">
          <HelpCircle className="w-4 h-4 text-[var(--math-purple)]" />
          <span className="text-xs font-medium text-[var(--math-purple)]">What's next?</span>
        </div>
      </motion.div>

      {/* Pattern Display - Compact */}
      <motion.div 
        className="flex items-center justify-center gap-2 mb-4 flex-wrap"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        {pattern.map((item, index) => (
          <motion.div
            key={index}
            className={`
              w-11 h-11 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center
              text-lg sm:text-xl font-bold shadow-sm
              ${item === '___' 
                ? 'bg-[var(--math-accent)]/30 border-2 border-dashed border-[var(--math-accent)]' 
                : 'bg-[var(--math-surface-elevated)]'
              }
            `}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05, duration: 0.15 }}
          >
            {item === '___' ? (
              <motion.span
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                ?
              </motion.span>
            ) : (
              item
            )}
          </motion.div>
        ))}
      </motion.div>

      {/* Options - 3 columns */}
      <div className="grid grid-cols-3 gap-3">
        {options.map((answer, index) => {
          const isSelected = selectedAnswer === answer
          const showAsCorrect = showFeedback && answer === correctAnswer
          const showAsWrong = showFeedback && isSelected && answer !== correctAnswer

          return (
            <motion.button
              key={answer}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 + index * 0.05, duration: 0.2 }}
              whileHover={!showFeedback ? { scale: 1.05 } : {}}
              whileTap={!showFeedback ? { scale: 0.95 } : {}}
              onClick={() => handleSelect(answer)}
              disabled={showFeedback}
              className={`
                h-16 rounded-xl font-bold text-xl sm:text-2xl
                flex items-center justify-center
                transition-all duration-150
                ${showAsCorrect 
                  ? 'bg-[var(--math-success)] text-white shadow-lg' 
                  : showAsWrong 
                    ? 'bg-[var(--math-error)] text-white'
                    : isSelected 
                      ? 'bg-[var(--math-purple)] text-white shadow-md'
                      : 'bg-white shadow-sm hover:shadow-md border-2 border-gray-100'
                }
              `}
            >
              {answer}
            </motion.button>
          )
        })}
      </div>

      {/* Enhanced Feedback with Countdown */}
      <AnimatePresence>
        {showFeedback && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-center mt-3"
          >
            {isCorrect ? (
              <div className="flex items-center justify-center gap-2 bg-[var(--math-success)]/10 px-4 py-2 rounded-full">
                <Sparkles className="w-5 h-5 text-[var(--math-success)]" />
                <span className="font-bold text-[var(--math-success)]">Pattern solved! {correctAnswer}!</span>
                {countdown > 0 && (
                  <span className="text-xs text-[var(--math-success)]/60 ml-1">{countdown}</span>
                )}
              </div>
            ) : (
              <div className="bg-[var(--math-error)]/10 px-3 py-2 rounded-xl">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-sm text-gray-500">You picked:</span>
                  <span className="font-bold text-[var(--math-error)]">{selectedAnswer}</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <span className="text-sm text-gray-500">Correct:</span>
                  <span className="font-bold text-[var(--math-success)]">{correctAnswer}</span>
                </div>
                <div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden max-w-[120px] mx-auto">
                  <motion.div
                    className="h-full bg-[var(--math-error)]"
                    initial={{ width: '100%' }}
                    animate={{ width: '0%' }}
                    transition={{ duration: 2, ease: 'linear' }}
                  />
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
